import React from 'react';
import './Timeline.css';

/**
 * Timeline — design-tool-style vertical scrubber.
 *
 * Props:
 *   messages   (array)    [{ ts: number, ... }, ...] sorted by ts ascending
 *   onChange   (function) (ts, message) => void
 *   initialTs  (number)   optional starting timestamp; defaults to latest
 *
 * Layout:
 *   ┌─────────┐
 *   │   2.5×  │  ← zoom chip (click to reset when zoomed)
 *   ├─────────┤
 *   │ 12:30 ─┊│
 *   │       ─┊│  ← time ruler + track + message events
 *   │ 12:35 ─┊│
 *   │═══════╪════ 12:34 PM   ← playhead: line across + readout flag
 *   │ 12:40 ─┊│
 *   └─────────┘
 */
export class Timeline extends React.Component {
  constructor(props) {
    super(props);
    const last = this.lastTs(props.messages);
    this.state = {
      playheadTs: props.initialTs != null ? props.initialTs : last,
      isDragging: false,
      zoom: 1,
      scrollPct: 0,
    };
    this.bodyRef = React.createRef();
  }

  componentDidMount() {
    document.addEventListener('mousemove', this.handleMove);
    document.addEventListener('mouseup', this.handleEnd);
    document.addEventListener('touchmove', this.handleMove, { passive: false });
    document.addEventListener('touchend', this.handleEnd);
    if (this.bodyRef.current) {
      this.bodyRef.current.addEventListener('wheel', this.handleWheel, { passive: false });
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousemove', this.handleMove);
    document.removeEventListener('mouseup', this.handleEnd);
    document.removeEventListener('touchmove', this.handleMove);
    document.removeEventListener('touchend', this.handleEnd);
    if (this.bodyRef.current) {
      this.bodyRef.current.removeEventListener('wheel', this.handleWheel);
    }
  }

  componentDidUpdate(prevProps) {
    const prevLast = this.lastTs(prevProps.messages);
    const newLast = this.lastTs(this.props.messages);
    if (newLast !== prevLast && this.state.playheadTs === prevLast) {
      this.setState({ playheadTs: newLast });
    }
  }

  // ---- math helpers ----

  lastTs = (messages) => (messages && messages.length ? messages[messages.length - 1].ts : 0);

  getRange = () => {
    const { messages } = this.props;
    if (!messages || !messages.length) return { start: 0, end: 1 };
    return { start: messages[0].ts, end: messages[messages.length - 1].ts };
  };

  tsToFullPct = (ts) => {
    const { start, end } = this.getRange();
    if (end === start) return 0;
    return ((ts - start) / (end - start)) * 100;
  };

  tsToVisiblePct = (ts) => {
    const { zoom, scrollPct } = this.state;
    return (this.tsToFullPct(ts) - scrollPct * 100) * zoom;
  };

  clientYToTs = (clientY) => {
    const body = this.bodyRef.current;
    if (!body) return 0;
    const rect = body.getBoundingClientRect();
    const visiblePct = Math.max(0, Math.min(100, ((clientY - rect.top) / rect.height) * 100));
    const fullPct = visiblePct / this.state.zoom + this.state.scrollPct * 100;
    const { start, end } = this.getRange();
    const ts = start + (fullPct / 100) * (end - start);
    return this.snapToNearestMessage(ts);
  };

  snapToNearestMessage = (ts) => {
    const { messages } = this.props;
    if (!messages || !messages.length) return ts;
    let best = messages[0];
    let bestDist = Math.abs(messages[0].ts - ts);
    for (let i = 1; i < messages.length; i++) {
      const d = Math.abs(messages[i].ts - ts);
      if (d < bestDist) {
        bestDist = d;
        best = messages[i];
        if (best.role === 'user') {
          best = messages[i+1]
        }
      }
    }
    console.log("snapToNearestMessage", { ts, best })
    return best.ts;
  };

  // Pick a "nice" interval for time-axis labels so we get ~5-7 of them visible.
  pickInterval = (visibleMs) => {
    const INTERVALS = [
      1000, 5000, 10000, 30000,
      60_000, 2*60_000, 5*60_000, 10*60_000, 15*60_000, 30*60_000,
      60*60_000, 2*60*60_000, 6*60*60_000, 12*60*60_000,
      24*60*60_000, 7*24*60*60_000,
    ];
    const target = visibleMs / 6;
    return INTERVALS.find(i => i >= target) || INTERVALS[INTERVALS.length - 1];
  };

  computeTicks = () => {
    const { start, end } = this.getRange();
    if (end === start) return { ticks: [], interval: 60000 };
    const { zoom, scrollPct } = this.state;
    const fullSpan = end - start;
    const visibleSpan = fullSpan / zoom;
    const visibleStart = start + scrollPct * fullSpan;
    const visibleEnd = visibleStart + visibleSpan;
    const interval = this.pickInterval(visibleSpan);
    const minor = interval / 2;

    const ticks = [];
    const firstMinor = Math.ceil(visibleStart / minor) * minor;
    for (let t = firstMinor; t <= visibleEnd; t += minor) {
      const isMajor = Math.round(t / interval) * interval === t;
      ticks.push({ ts: t, pct: this.tsToVisiblePct(t), isMajor });
    }
    return { ticks, interval };
  };

  formatLabel = (ts, interval) => {
    const d = new Date(ts);
    if (interval >= 24*60*60_000) {
      return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
    }
    // HH:MM in 24h-ish format — short and predictable for a ruler
    const h = d.getHours();
    const m = String(d.getMinutes()).padStart(2, '0');
    if (interval < 60_000) {
      const s = String(d.getSeconds()).padStart(2, '0');
      return `${h}:${m}:${s}`;
    }
    return `${h}:${m}`;
  };

  formatPlayhead = (ts) => {
    const d = new Date(ts);
    const h = d.getHours();
    const m = String(d.getMinutes()).padStart(2, '0');
    return `${h}:${m}`;
  };

  // ---- event handlers ----

  getClientY = (e) => {
    if (e.touches && e.touches[0]) return e.touches[0].clientY;
    if (e.changedTouches && e.changedTouches[0]) return e.changedTouches[0].clientY;
    return e.clientY;
  };

  handleStart = (e) => {
    e.preventDefault();
    const ts = this.clientYToTs(this.getClientY(e));
    this.setState({ isDragging: true, playheadTs: ts });
    this.emitChange(ts);
  };

  handleMove = (e) => {
    if (!this.state.isDragging) return;
    if (e.cancelable) e.preventDefault();
    const ts = this.clientYToTs(this.getClientY(e));
    if (ts !== this.state.playheadTs) {
      this.setState({ playheadTs: ts });
      this.emitChange(ts);
    }
  };

  handleEnd = () => {
    if (this.state.isDragging) this.setState({ isDragging: false });
  };

  handleWheel = (e) => {
    e.preventDefault();
    const body = this.bodyRef.current;
    if (!body) return;
    const rect = body.getBoundingClientRect();
    const cursorVisiblePct = ((e.clientY - rect.top) / rect.height) * 100;

    const { zoom, scrollPct } = this.state;
    const cursorFullPct = cursorVisiblePct / zoom + scrollPct * 100;

    const factor = e.deltaY < 0 ? 1.2 : 1 / 1.2;
    const newZoom = Math.max(1, Math.min(50, zoom * factor));

    const maxScroll = Math.max(0, 1 - 1 / newZoom);
    const newScrollPct = Math.max(0, Math.min(
      maxScroll,
      (cursorFullPct - cursorVisiblePct / newZoom) / 100
    ));

    this.setState({ zoom: newZoom, scrollPct: newScrollPct });
  };

  resetZoom = (e) => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ zoom: 1, scrollPct: 0 });
  };

  emitChange = (ts) => {
    if (this.props.onChange) {
      const msg = this.props.messages.find(m => m.ts === ts);
      this.props.onChange(ts, msg);
    }
  };

  render() {
    const { messages = [] } = this.props;
    const { playheadTs, isDragging, zoom, scrollPct } = this.state;
    const pct = this.tsToVisiblePct(playheadTs);
    const inView = pct >= 0 && pct <= 100;
    const isAtEnd = playheadTs === this.lastTs(messages);
    const zoomed = zoom > 1.01;

    const { ticks, interval } = this.computeTicks();
    const hasContentAbove = zoomed && scrollPct > 0.001;
    const hasContentBelow = zoomed && scrollPct < (1 - 1 / zoom) - 0.001;

    return (
      <div className={`tl ${isDragging ? 'tl--dragging' : ''} ${isAtEnd ? 'tl--present' : 'tl--past'}`}>
        {/* Header: zoom chip */}
        <div className="tl__header">
          {zoomed ? (
            <button
              className="tl__zoom-chip tl__zoom-chip--active"
              onClick={this.resetZoom}
              title="Reset zoom"
            >
              {zoom.toFixed(1)} <span className="tl__zoom-x">×</span>
            </button>
          ) : (
            <span className="tl__zoom-chip">1×</span>
          )}
        </div>

        {/* Body: ruler + track */}
        <div
          ref={this.bodyRef}
          className="tl__body"
          onMouseDown={this.handleStart}
          onTouchStart={this.handleStart}
        >
          {hasContentAbove && <div className="tl__edge tl__edge--top">▴</div>}
          {hasContentBelow && <div className="tl__edge tl__edge--bottom">▾</div>}

          {/* Ticks + labels */}
          {ticks.map(({ ts, pct: tp, isMajor }) => {
            if (tp < 0 || tp > 100) return null;
            return (
              <React.Fragment key={ts}>
                <div
                  className={`tl__tick ${isMajor ? 'tl__tick--major' : 'tl__tick--minor'}`}
                  style={{ top: `${tp}%` }}
                />
                {isMajor && (
                  <div className="tl__label" style={{ top: `${tp}%` }}>
                    {this.formatLabel(ts, interval)}
                  </div>
                )}
              </React.Fragment>
            );
          })}

          {/* Track line */}
          <div className="tl__track" />

          {/* Message events */}
          {messages.map((m, i) => {
            const ep = this.tsToVisiblePct(m.ts);
            if (ep < 0 || ep > 100) return null;
            const isPast = m.ts <= playheadTs;
            return (
              <div
                key={i}
                className={`tl__event ${isPast ? 'tl__event--past' : ''}`}
                style={{ top: `${ep}%` }}
              />
            );
          })}

          {/* Playhead */}
          {inView && (
            <div className="tl__playhead" style={{ top: `${pct}%` }}>
              <div className="tl__playhead-line" />
              <div className="tl__playhead-flag">
                {this.formatPlayhead(playheadTs)}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }
}


